import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { omnimizerApi } from "./api";
import { useGpuConflictDialog } from "./components/GpuConflictDialog";
import type { DeploymentDuplicate } from "./types";

/** Keep the original request/catalog intent while users resolve admission. */
export function useDeploymentGuard() {
  const { t } = useTranslation();
  const { resolveConflict, dialog } = useGpuConflictDialog();
  const [duplicate, setDuplicate] = useState<{ deployment: DeploymentDuplicate; resolve: (proceed: boolean) => void }>();
  const mounted = useRef(true); const resolver = useRef<((proceed: boolean) => void)>();
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; resolver.current?.(false); }; }, []);
  async function run<T>(payload: { model_url?: string }, create: () => Promise<T>): Promise<T | undefined> {
    const rows = await omnimizerApi.findDuplicates(payload.model_url || "");
    if (!mounted.current) return;
    const existing = rows.find((d) => d.model_url === payload.model_url);
    if (existing) {
      const proceed = await new Promise<boolean>((resolve) => {
        resolver.current = resolve; setDuplicate({ deployment: existing, resolve });
      });
      if (!proceed || !mounted.current) return;
    }
    while (mounted.current) {
      try {
        await omnimizerApi.checkAdmission(payload);
        if (!mounted.current) return;
        return await create();
      } catch (failure) {
        const decision = resolveConflict(failure);
        if (!decision) throw failure;
        if (!await decision || !mounted.current) return;
      }
    }
  }
  function finish(proceed: boolean) {
    duplicate?.resolve(proceed); resolver.current = undefined; setDuplicate(undefined);
  }
  const dialogs = <>{dialog}<Dialog open={Boolean(duplicate)} onOpenChange={(open) => { if (!open) finish(false); }}>
    <DialogContent><DialogHeader><DialogTitle>{t("omnimizer.lifecycle.duplicateTitle")}</DialogTitle>
      <DialogDescription>{t("omnimizer.lifecycle.duplicatePrompt", { name: duplicate?.deployment.name })}</DialogDescription>
    </DialogHeader><DialogFooter>
      <Button variant="outline" onClick={() => finish(false)}>{t("omnimizer.lifecycle.cancel")}</Button>
      {duplicate?.deployment.can_open ? <Button variant="outline" asChild><a href={`/deployments/${duplicate.deployment.model_id}`} onClick={() => finish(false)}>{t("omnimizer.lifecycle.openExisting")}</a></Button>
        : <Button variant="outline" disabled title={t("omnimizer.lifecycle.askOwner")}>{t("omnimizer.lifecycle.openExisting")}</Button>}
      <Button onClick={() => finish(true)}>{t("omnimizer.lifecycle.deployAnyway")}</Button>
    </DialogFooter></DialogContent>
  </Dialog></>;
  return { run, dialogs };
}
